import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getTeacherBillingSubscription,
  postTeacherBillingCheckoutSession,
  postTeacherBillingPortalSession,
  type TeacherBillingSubscriptionResponse,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { BillingKpiGrid } from "./BillingKpiGrid";
import {
  TEACHER_PAID_FEATURES_LINE,
  TEACHER_PAID_LIMITS_LINE,
  TEACHER_TRIAL_LIMITS_LINE,
  TEACHER_TRIAL_VS_STRIPE_NOTE,
} from "../marketing/teacher-billing-copy";

function formatDate(iso: string | null): string {
  if (!iso) return "n/a";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function daysRemaining(iso: string | null): number | null {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  const now = Date.now();
  return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
}

export default function TeacherBillingPage() {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"portal" | "monthly" | "annual" | null>(null);
  const [data, setData] = useState<TeacherBillingSubscriptionResponse | null>(null);

  const refresh = useCallback(async () => {
    if (!bearer) return;
    setLoading(true);
    setError(null);
    try {
      const next = await getTeacherBillingSubscription(bearer);
      setData(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load billing status");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [bearer]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function startCheckout(plan: "monthly" | "annual") {
    if (!bearer) return;
    setBusy(plan);
    setError(null);
    try {
      const { checkoutUrl } = await postTeacherBillingCheckoutSession(bearer, { plan });
      window.location.href = checkoutUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start checkout");
      setBusy(null);
    }
  }

  async function openPortal() {
    if (!bearer) return;
    setBusy("portal");
    setError(null);
    try {
      const { portalUrl } = await postTeacherBillingPortalSession(bearer);
      window.location.href = portalUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not open billing portal");
    } finally {
      setBusy(null);
    }
  }

  const phase = data?.accessPhase;
  const paid = phase === "paid";
  const expired = phase === "expired";
  const trialDays = daysRemaining(data?.appTrial?.endsAt ?? null);

  return (
    <div className="teacher-page submissions-dashboard teacher-billing-page">
      <header className="submissions-hero overview-hero">
        <div className="submissions-hero-text">
          <p className="eyebrow">Teacher · Account</p>
          <h1 className="teacher-page-title">Billing</h1>
          <p className="teacher-page-lede">
            See what you are teaching, what you have built, and how much room is left on your plan.
          </p>
        </div>
      </header>

      {error ? (
        <div className="warn-banner" role="alert">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : paid ? (
        <PaidView
          data={data!}
          busy={busy === "portal" ? "portal" : null}
          onOpenPortal={() => void openPortal()}
        />
      ) : (
        <UpgradeView
          expired={expired}
          trialDays={trialDays}
          trialEndsAt={data?.appTrial?.endsAt ?? null}
          data={data}
          busy={busy === "monthly" || busy === "annual" ? busy : null}
          onStartCheckout={(plan) => void startCheckout(plan)}
        />
      )}
    </div>
  );
}

function formatAmount(unitAmount: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(unitAmount / 100);
}

function planLabel(plan: TeacherBillingSubscriptionResponse["plan"]): string {
  if (!plan) return "Teacher Pro";
  const freq = plan.interval === "year" ? "Annual" : "Monthly";
  return `Teacher Pro (${freq})`;
}

function planPrice(plan: TeacherBillingSubscriptionResponse["plan"]): string | null {
  if (!plan) return null;
  const amt = formatAmount(plan.unitAmount, plan.currency);
  return plan.interval === "year" ? `${amt} / year` : `${amt} / month`;
}

function PaidView({
  data,
  busy,
  onOpenPortal,
}: {
  data: TeacherBillingSubscriptionResponse;
  busy: "portal" | null;
  onOpenPortal: () => void;
}) {
  const { subscription, plan } = data;
  const cancelling = subscription.cancelAtPeriodEnd;
  const renewsLabel = cancelling ? "Cancels on" : "Renews on";
  const renewsDate = formatDate(subscription.currentPeriodEnd);

  return (
    <div className="teacher-billing-paid">
      <div className="teacher-billing-plan-hero">
        <div className="teacher-billing-plan-hero-top">
          <div>
            <p className="teacher-billing-plan-hero-name">{planLabel(plan)}</p>
            {planPrice(plan) && <p className="teacher-billing-plan-hero-price">{planPrice(plan)}</p>}
            <p className="muted teacher-billing-plan-limits">{TEACHER_PAID_LIMITS_LINE}.</p>
          </div>
          <span className="teacher-billing-active-badge">Active</span>
        </div>

        <div className="teacher-billing-renews">
          {cancelling ? (
            <p className="teacher-billing-renews-label teacher-billing-renews-label--cancel">
              {renewsLabel} {renewsDate}
            </p>
          ) : (
            <p className="teacher-billing-renews-label">
              {renewsLabel} {renewsDate}
            </p>
          )}
          {subscription.currentPeriodStart && subscription.currentPeriodEnd && (
            <p className="teacher-billing-renews-period muted">
              Current period: {formatDate(subscription.currentPeriodStart)} –{" "}
              {formatDate(subscription.currentPeriodEnd)}
            </p>
          )}
        </div>

        <div className="teacher-billing-portal-row">
          <button
            type="button"
            className="btn secondary"
            disabled={busy !== null}
            onClick={onOpenPortal}
          >
            {busy === "portal" ? "Opening…" : "Manage subscription"}
          </button>
          <p className="muted teacher-billing-portal-hint">
            Update payment method, download invoices, or cancel.
          </p>
        </div>
      </div>

      <BillingKpiGrid data={data} aiTitle="AI this month" />
    </div>
  );
}

function UpgradeView({
  expired,
  trialDays,
  trialEndsAt,
  data,
  busy,
  onStartCheckout,
}: {
  expired: boolean;
  trialDays: number | null;
  trialEndsAt: string | null;
  data: TeacherBillingSubscriptionResponse | null;
  busy: "monthly" | "annual" | null;
  onStartCheckout: (plan: "monthly" | "annual") => void;
}) {
  return (
    <div className="teacher-billing-upgrade">
      <div
        className={`teacher-billing-status-card ${expired ? "teacher-billing-status-card--expired" : "teacher-billing-status-card--trial"}`}
      >
        {expired ? (
          <>
            <p className="teacher-billing-status-label">Your trial has ended</p>
            <p className="teacher-billing-status-detail">
              You can still use the <Link to="/teacher/sandbox">sandbox IDE</Link> to explore. Subscribe
              below to unlock paid plan limits.
            </p>
          </>
        ) : (
          <>
            <p className="teacher-billing-status-label">
              {trialDays != null && trialDays > 0
                ? `Free trial: ${trialDays} day${trialDays === 1 ? "" : "s"} remaining`
                : "Free trial active"}
            </p>
            <p className="teacher-billing-status-detail">
              {trialEndsAt ? `Trial ends ${formatDate(trialEndsAt)}.` : ""} No card required. During your
              15-day trial: {TEACHER_TRIAL_LIMITS_LINE}.
            </p>
          </>
        )}
      </div>

      {data && !expired ? <BillingKpiGrid data={data} aiTitle="AI during trial" /> : null}

      <div className="teacher-billing-plans">
        <div className="teacher-billing-plan-card">
          <h2 className="teacher-billing-plan-name">Monthly</h2>
          <p className="teacher-billing-plan-price">
            $19 <span className="teacher-billing-plan-period">/ month</span>
          </p>
          <p className="teacher-billing-plan-detail muted">
            {TEACHER_PAID_FEATURES_LINE}. Billed monthly, cancel any time.
          </p>
          <button
            type="button"
            className="btn"
            disabled={busy !== null}
            onClick={() => onStartCheckout("monthly")}
          >
            {busy === "monthly" ? "Redirecting to checkout…" : "Get started monthly"}
          </button>
        </div>

        <div className="teacher-billing-plan-card teacher-billing-plan-card--featured">
          <div className="teacher-billing-plan-badge">Best value</div>
          <h2 className="teacher-billing-plan-name">Annual</h2>
          <p className="teacher-billing-plan-price">
            $199 <span className="teacher-billing-plan-period">/ year</span>
          </p>
          <p className="teacher-billing-plan-detail muted">
            Same paid access, about $16.60/month billed once a year. {TEACHER_PAID_FEATURES_LINE}.
          </p>
          <button
            type="button"
            className="btn primary"
            disabled={busy !== null}
            onClick={() => onStartCheckout("annual")}
          >
            {busy === "annual" ? "Redirecting to checkout…" : "Get started annually"}
          </button>
        </div>
      </div>

      <p className="teacher-billing-reassurance muted">
        {TEACHER_TRIAL_VS_STRIPE_NOTE} Checkout is handled by Stripe. You can cancel or change your plan at
        any time from your billing portal.
      </p>
    </div>
  );
}
